import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../shared/context/AuthContext";
import { stationsAPI } from "../../../shared/lib/api";
import { Button } from "../../../shared/components/ui/button";
import { Input } from "../../../shared/components/ui/input";
import { Label } from "../../../shared/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../../shared/components/ui/card";
import { Badge } from "../../../shared/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../shared/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../shared/components/ui/select";
import { toast } from "sonner";
import {
  Building2,
  Plus,
  Search,
  Edit,
  Power,
  PowerOff,
  ArrowLeft,
  Upload,
  FileSpreadsheet,
} from "lucide-react";
import { formatDateTime } from "../../../shared/lib/utils";

export default function StationsManagerPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStation, setEditingStation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [importing, setImporting] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Form state
  const [stationName, setStationName] = useState("");
  const [stationCode, setStationCode] = useState("");
  const [region, setRegion] = useState("");
  const [description, setDescription] = useState("");
  // New fields
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [stationState, setStationState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [gpsLat, setGpsLat] = useState("");
  const [gpsLng, setGpsLng] = useState("");
  const [directionsNotes, setDirectionsNotes] = useState("");
  const [mapReference, setMapReference] = useState("");

  useEffect(() => {
    if (user?.role !== "admin") {
      toast.error("Admin access required");
      navigate("/");
      return;
    }
    loadStations();
  }, [statusFilter]);

  const loadStations = async () => {
    try {
      const params = { include_inactive: true };
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }
      const res = await stationsAPI.getAll(params);
      setStations(res.data);
    } catch (err) {
      toast.error("Failed to load stations");
    } finally {
      setLoading(false);
    }
  };

  const filteredStations = stations.filter((s) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      s.station_name.toLowerCase().includes(query) ||
      (s.station_code && s.station_code.toLowerCase().includes(query)) ||
      (s.region && s.region.toLowerCase().includes(query))
    );
  });

  const resetForm = () => {
    setStationName("");
    setStationCode("");
    setRegion("");
    setDescription("");
    setStreetAddress("");
    setCity("");
    setStationState("");
    setZipCode("");
    setGpsLat("");
    setGpsLng("");
    setDirectionsNotes("");
    setMapReference("");
    setEditingStation(null);
  };

  const openEditDialog = (station) => {
    setEditingStation(station);
    setStationName(station.station_name);
    setStationCode(station.station_code || "");
    setRegion(station.region || "");
    setDescription(station.description || "");
    setStreetAddress(station.street_address || "");
    setCity(station.city || "");
    setStationState(station.state || "");
    setZipCode(station.zip_code || "");
    setGpsLat(station.gps_lat?.toString() || "");
    setGpsLng(station.gps_lng?.toString() || "");
    setDirectionsNotes(station.directions_notes || "");
    setMapReference(station.map_reference || "");
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stationName) {
      toast.error("Station name is required");
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        station_name: stationName,
        station_code: stationCode || null,
        region: region || null,
        description: description || null,
        street_address: streetAddress || null,
        city: city || null,
        state: stationState || null,
        zip_code: zipCode || null,
        gps_lat: gpsLat ? parseFloat(gpsLat) : null,
        gps_lng: gpsLng ? parseFloat(gpsLng) : null,
        directions_notes: directionsNotes || null,
        map_reference: mapReference || null,
      };

      if (editingStation) {
        await stationsAPI.update(editingStation.id, data);
        toast.success("Station updated");
      } else {
        await stationsAPI.create(data);
        toast.success("Station created");
      }

      setDialogOpen(false);
      resetForm();
      loadStations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save station");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (station) => {
    try {
      if (station.status === "active") {
        await stationsAPI.deactivate(station.id);
        toast.success("Station deactivated");
      } else {
        await stationsAPI.reactivate(station.id);
        toast.success("Station reactivated");
      }
      loadStations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    }
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const res = await stationsAPI.importCSV(file);
      const { created_offices, upserted_stations, skipped_rows, errors } = res.data;
      
      toast.success(
        `Import complete: ${upserted_stations} stations imported, ${created_offices} offices created${skipped_rows > 0 ? `, ${skipped_rows} rows skipped` : ""}`
      );
      
      if (errors && errors.length > 0) {
        console.warn("Import errors:", errors);
      }
      
      loadStations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to import CSV");
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner h-8 w-8 border-[#FF6B00]" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" data-testid="stations-manager-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin")}
            className="font-mono text-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="font-heading font-black text-2xl flex items-center gap-2">
            <Building2 className="h-6 w-6 text-[#FF6B00]" />
            Stations Manager
          </h1>
        </div>

        <div className="flex items-center gap-3">
          {/* CSV Import Button */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImportCSV}
            accept=".csv"
            className="hidden"
            data-testid="csv-import-input"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="h-12 rounded-sm border-2 font-mono uppercase tracking-wider"
            data-testid="import-csv-btn"
          >
            {importing ? (
              <span className="spinner" />
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Import CSV
              </>
            )}
          </Button>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button
                className="h-12 rounded-sm bg-[#FF6B00] hover:bg-[#E65100] font-mono uppercase tracking-wider"
                data-testid="add-station-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Station
              </Button>
            </DialogTrigger>
          <DialogContent className="rounded-none border-2 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingStation ? "Edit Station" : "Add Station"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                    Station Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={stationName}
                    onChange={(e) => setStationName(e.target.value)}
                    placeholder="Main Substation Alpha"
                    className="h-11 rounded-none border-2 font-mono"
                    data-testid="station-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                    Station Code
                  </Label>
                  <Input
                    value={stationCode}
                    onChange={(e) => setStationCode(e.target.value)}
                    placeholder="SS-001"
                    className="h-11 rounded-none border-2 font-mono"
                    data-testid="station-code-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                    Region
                  </Label>
                  <Input
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    placeholder="Downtown"
                    className="h-11 rounded-none border-2 font-mono"
                    data-testid="station-region-input"
                  />
                </div>

                <div className="space-y-2 col-span-2">
                  <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                    Description
                  </Label>
                  <Input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Primary distribution substation"
                    className="h-11 rounded-none border-2 font-mono"
                    data-testid="station-description-input"
                  />
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <p className="font-mono text-xs uppercase tracking-widest font-bold mb-3 text-muted-foreground">
                  Address Information
                </p>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                      Street Address
                    </Label>
                    <Input
                      value={streetAddress}
                      onChange={(e) => setStreetAddress(e.target.value)}
                      placeholder="1234 Power Line Road"
                      className="h-11 rounded-none border-2 font-mono"
                      data-testid="station-street-address-input"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                        City
                      </Label>
                      <Input
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder="Houston"
                        className="h-11 rounded-none border-2 font-mono"
                        data-testid="station-city-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                        State
                      </Label>
                      <Input
                        value={stationState}
                        onChange={(e) => setStationState(e.target.value)}
                        placeholder="TX"
                        className="h-11 rounded-none border-2 font-mono"
                        data-testid="station-state-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                        ZIP Code
                      </Label>
                      <Input
                        value={zipCode}
                        onChange={(e) => setZipCode(e.target.value)}
                        placeholder="77001"
                        className="h-11 rounded-none border-2 font-mono"
                        data-testid="station-zip-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <p className="font-mono text-xs uppercase tracking-widest font-bold mb-3 text-muted-foreground">
                  GPS Coordinates & Maps
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                      Latitude
                    </Label>
                    <Input
                      type="number"
                      step="any"
                      value={gpsLat}
                      onChange={(e) => setGpsLat(e.target.value)}
                      placeholder="29.7604"
                      className="h-11 rounded-none border-2 font-mono"
                      data-testid="station-gps-lat-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                      Longitude
                    </Label>
                    <Input
                      type="number"
                      step="any"
                      value={gpsLng}
                      onChange={(e) => setGpsLng(e.target.value)}
                      placeholder="-95.3698"
                      className="h-11 rounded-none border-2 font-mono"
                      data-testid="station-gps-lng-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                      Map Reference
                    </Label>
                    <Input
                      value={mapReference}
                      onChange={(e) => setMapReference(e.target.value)}
                      placeholder="MAP-001"
                      className="h-11 rounded-none border-2 font-mono"
                      data-testid="station-map-ref-input"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label className="font-mono text-xs uppercase tracking-widest font-bold">
                      Directions & Notes
                    </Label>
                    <Input
                      value={directionsNotes}
                      onChange={(e) => setDirectionsNotes(e.target.value)}
                      placeholder="Turn left after the main gate..."
                      className="h-11 rounded-none border-2 font-mono"
                      data-testid="station-directions-input"
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 rounded-sm bg-[#FF6B00] hover:bg-[#E65100] font-mono uppercase tracking-wider font-bold"
                data-testid="submit-station-btn"
              >
                {submitting ? <span className="spinner" /> : editingStation ? "Update Station" : "Add Station"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-2 border-border rounded-none mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-widest">
                Search
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search stations..."
                  className="h-10 rounded-none border-2 font-mono pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-widest">
                Status
              </Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-10 rounded-none border-2 font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <div className="font-mono text-sm text-muted-foreground">
                {filteredStations.length} station(s)
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stations List */}
      <div className="space-y-3">
        {filteredStations.map((station) => (
          <Card
            key={station.id}
            className={`border-2 rounded-none ${
              station.status === "inactive" ? "border-slate-300 bg-slate-50 dark:bg-slate-900/50" : "border-border"
            }`}
            data-testid={`station-item-${station.id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-heading font-bold text-lg">
                      {station.station_name}
                    </span>
                    {station.station_code && (
                      <Badge variant="outline" className="rounded-none font-mono text-xs">
                        {station.station_code}
                      </Badge>
                    )}
                    <Badge
                      className={`rounded-none font-mono text-xs ${
                        station.status === "active"
                          ? "bg-emerald-500 text-white"
                          : "bg-slate-500 text-white"
                      }`}
                    >
                      {station.status.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    {station.region && <span>Region: {station.region}</span>}
                    {station.description && <span>{station.description}</span>}
                  </div>
                  {(station.street_address || station.city) && (
                    <div className="mt-1 text-sm text-muted-foreground">
                      {[station.street_address, station.city, station.state, station.zip_code].filter(Boolean).join(", ")}
                    </div>
                  )}
                  {(station.gps_lat && station.gps_lng) && (
                    <div className="mt-1 font-mono text-xs text-blue-600 dark:text-blue-400">
                      GPS: {station.gps_lat}, {station.gps_lng}
                    </div>
                  )}
                  <div className="mt-2 font-mono text-xs text-muted-foreground">
                    Created: {formatDateTime(station.created_at)}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(station)}
                    className="rounded-none border-2 font-mono text-xs"
                    data-testid={`edit-station-${station.id}`}
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleStatus(station)}
                    className={`rounded-none border-2 font-mono text-xs ${
                      station.status === "active"
                        ? "text-amber-600 border-amber-500"
                        : "text-emerald-600 border-emerald-500"
                    }`}
                    data-testid={`toggle-station-${station.id}`}
                  >
                    {station.status === "active" ? (
                      <>
                        <PowerOff className="h-3 w-3 mr-1" />
                        Deactivate
                      </>
                    ) : (
                      <>
                        <Power className="h-3 w-3 mr-1" />
                        Reactivate
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
